import React, { useEffect, useRef, useState } from "react";
import {
  Microphone, PencilSimple, ArrowDown, Check, Calendar,
  WhatsappLogo, Bell, MapPin,
} from "@phosphor-icons/react";

/**
 * HeroAgendaMockup — racconto in 3 step per la sezione "Agenda":
 *   In cima: due ingressi affiancati ("Tieni premuto" / "Scrivi") che fanno
 *   capire subito che puoi alimentare l'agenda PARLANDO o SCRIVENDO.
 *   1. Una frase compare nella bolla (qui simulo il vocale, ma la stessa
 *      bolla potrebbe arrivare anche dalla tastiera).
 *   2. Pronto estrae i dati (chips: appuntamento · indirizzo · contatti).
 *   3. L'evento viene creato sul calendario.
 *
 * Animazione: 3 step in fade lento, una sola volta. Layout già allocato per
 * evitare scatti su mobile.
 */
const PHRASE = "Domani alle 10, visita in Via Brera con i Ferrari, trilocale, budget 500k.";

export default function HeroAgendaMockup({ reelMode = false } = {}) {
  const rootRef = useRef(null);
  // Iter 232: bypass observer per evitare lag dopo click su chip hero.
  const [visible, setVisible] = useState(true);
  const [step, setStep] = useState(0);
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (!rootRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.25 },
    );
    obs.observe(rootRef.current);
    return () => obs.disconnect();
  }, []);

  // 0.4s → step 1 · 2.6s → step 2 · 4.6s → step 3 · (reelMode) 6.6s → step 4
  useEffect(() => {
    if (!visible) return;
    let mounted = true;
    const tids = [];
    tids.push(setTimeout(() => { if (mounted) setStep(1); }, 400));
    tids.push(setTimeout(() => { if (mounted) setStep(2); }, 2600));
    tids.push(setTimeout(() => { if (mounted) setStep(3); }, 4600));
    if (reelMode) {
      tids.push(setTimeout(() => { if (mounted) setStep(4); }, 6600));
    }
    return () => { mounted = false; tids.forEach(clearTimeout); };
  }, [visible, reelMode]);

  // Typewriter: la frase si scrive lettera per lettera dentro la bolla.
  useEffect(() => {
    if (step < 1) return;
    let i = 0;
    setTyped("");
    const id = setInterval(() => {
      i += 1;
      setTyped(PHRASE.slice(0, i));
      if (i >= PHRASE.length) clearInterval(id);
    }, 28);
    return () => clearInterval(id);
  }, [step]);

  return (
    <div ref={rootRef} className="max-w-[440px] mx-auto" data-testid="hero-agenda-mockup">
      {/* Step 1 — Due ingressi + bolla che si scrive */}
      <RevealStep show={step >= 1}>
        <Step n={1} title="Tieni premuto e parla, o scrivi">
          <div className="grid grid-cols-2 gap-2 mb-2">
            <ToggleEntry icon={Microphone} label="Tieni premuto" hint="parla" active />
            <ToggleEntry icon={PencilSimple} label="Scrivi" hint="testo" />
          </div>
          <div className="bg-[#F1F4ED] rounded-md px-2.5 py-2 min-h-[52px]">
            <div className="text-[9.5px] uppercase tracking-[0.14em] font-bold text-[#7C8B6E] mb-1">
              Pronto trascrive
            </div>
            <p className="text-[11.5px] text-[#1C1C1A] italic leading-snug">
              {typed}
              {step >= 1 && typed.length < PHRASE.length && (
                <span className="inline-block w-[6px] h-[12px] bg-[#7C8B6E] align-middle ml-[1px] animate-pulse" />
              )}
            </p>
          </div>
        </Step>
      </RevealStep>

      <RevealStep show={step >= 2}><Connector /></RevealStep>

      {/* Step 2 — Chip estratti */}
      <RevealStep show={step >= 2}>
        <Step n={2} title="Pronto capisce e separa i pezzi">
          <ChipsRow active={step >= 2} />
        </Step>
      </RevealStep>

      <RevealStep show={step >= 3}><Connector /></RevealStep>

      {/* Step 3 — Evento sul calendario */}
      <RevealStep show={step >= 3}>
        <Step n={3} title="L'evento è già nel calendario">
          <CalendarPreview active={step >= 3} />
        </Step>
      </RevealStep>

      {/* Step 4 — Solo reelMode: notifica WhatsApp + altri appuntamenti */}
      {reelMode && (
        <>
          <RevealStep show={step >= 4}><Connector /></RevealStep>
          <RevealStep show={step >= 4}>
            <Step n={4} title="Pronto ti ricorda · tutti gli impegni">
              <ExtraAgendaItems active={step >= 4} />
            </Step>
          </RevealStep>
        </>
      )}
    </div>
  );
}

function RevealStep({ show, children }) {
  return (
    <div
      className="transition-[opacity,transform] duration-[1600ms] ease-out will-change-transform"
      style={{
        opacity: show ? 1 : 0,
        transform: show ? "translateY(0)" : "translateY(12px)",
      }}
    >
      {children}
    </div>
  );
}

function ToggleEntry({ icon: Icon, label, hint, active = false }) {
  return (
    <div
      className="rounded-md border px-2.5 py-2 flex items-center gap-2 transition-colors"
      style={{
        background: active ? "#7C8B6E" : "#FFFFFF",
        borderColor: active ? "#7C8B6E" : "#E5E7E0",
        color: active ? "#FFFFFF" : "#1C1C1A",
      }}
    >
      <Icon size={15} weight="fill" className="shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] font-semibold leading-tight truncate">{label}</div>
        <div
          className="text-[9.5px] leading-tight"
          style={{ color: active ? "rgba(255,255,255,0.75)" : "#9A968B" }}
        >
          {hint}
        </div>
      </div>
    </div>
  );
}

function ChipsRow({ active }) {
  const CHIPS = [
    { tag: "Quando", val: "domani · 10:00" },
    { tag: "Dove", val: "Via Brera" },
    { tag: "Con", val: "Famiglia Ferrari" },
    { tag: "Tipo", val: "Visita cliente" },
  ];
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (!active) return;
    let mounted = true;
    const tids = CHIPS.map((_, i) =>
      setTimeout(() => mounted && setShown((s) => Math.max(s, i + 1)), 250 + i * 220),
    );
    return () => { mounted = false; tids.forEach(clearTimeout); };
  }, [active]);
  return (
    <div className="flex flex-wrap gap-1.5">
      {CHIPS.map((c, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full bg-white border border-[#7C8B6E]/40 text-[#1C1C1A] shadow-[0_1px_0_rgba(0,0,0,0.02)] transition-all duration-500"
          style={{
            opacity: shown > i ? 1 : 0,
            transform: shown > i ? "translateY(0)" : "translateY(4px)",
          }}
        >
          <span className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#5C6E50]">{c.tag}</span>
          <span className="text-[#1C1C1A] font-semibold">{c.val}</span>
        </span>
      ))}
    </div>
  );
}

function CalendarPreview({ active }) {
  return (
    <div className="bg-white border border-[#E5E7E0] rounded-md overflow-hidden">
      <div className="grid grid-cols-5 text-[9.5px] uppercase tracking-[0.14em] font-bold text-[#9A968B] border-b border-[#E5E7E0]">
        {["Lun", "Mar", "Mer", "Gio", "Ven"].map((d, i) => (
          <div key={d} className="px-2 py-1 text-center" style={{ background: i === 1 ? "#F1F4ED" : "transparent" }}>
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-5 relative">
        {[0,1,2,3,4].map((i) => (
          <div key={i} className="h-14 border-r border-[#E5E7E0] last:border-r-0 px-1 py-1 text-[8.5px] text-[#9A968B]">
            {i === 0 ? "10:00" : ""}
          </div>
        ))}
        {/* Evento creato sul Mar (col 2): occupa 1 colonna intera */}
        <div
          className="absolute top-1 bg-[#7C8B6E] text-white rounded px-1.5 py-1 transition-all duration-700 ease-out shadow-sm"
          style={{
            left: "calc(20% + 2px)",
            width: "calc(20% - 4px)",
            opacity: active ? 1 : 0,
            transform: active ? "scale(1)" : "scale(0.9)",
          }}
        >
          <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.1em]">
            <Calendar size={9} weight="fill" /> 10:00
          </div>
          <div className="text-[10px] font-semibold leading-tight mt-0.5">Visita</div>
          <div className="text-[9.5px] leading-tight opacity-90">Ferrari</div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 px-2 py-1.5 border-t border-[#E5E7E0] bg-[#F1F4ED]/50">
        <Check size={11} weight="bold" className="text-[#5C6E50]" />
        <span className="text-[10px] text-[#5C6E50] font-semibold">Aggiunto a Google Calendar e iCloud</span>
      </div>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="bg-white border border-[#E5E7E0] rounded-lg p-3 shadow-sm relative">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-6 h-6 rounded-full bg-[#7C8B6E] text-white text-[11px] font-bold flex items-center justify-center shrink-0">
          {n}
        </div>
        <div className="text-[12px] font-semibold text-[#1C1C1A] leading-tight">{title}</div>
      </div>
      {children}
    </div>
  );
}

function Connector() {
  return (
    <div className="flex justify-center -my-0.5 relative z-0">
      <div className="w-6 h-6 rounded-full bg-[#F1F4ED] border border-[#E5E7E0] flex items-center justify-center">
        <ArrowDown size={11} weight="bold" className="text-[#7C8B6E]" />
      </div>
    </div>
  );
}

/* ExtraAgendaItems — solo reelMode. Mostra in animazione:
   1) Notifica WhatsApp al cliente: "ricordo visita domani 10:00"
   2) Lista impegni della settimana animati uno per uno (firma, follow-up, sopralluogo)
   3) Badge "Sincronizzato" con Google + iCloud */
function ExtraAgendaItems({ active }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (!active) return;
    let mounted = true;
    const tids = [];
    [1, 2, 3, 4].forEach((i) => {
      tids.push(setTimeout(() => mounted && setShown((s) => Math.max(s, i)), 200 + (i - 1) * 350));
    });
    return () => { mounted = false; tids.forEach(clearTimeout); };
  }, [active]);

  const items = [
    { icon: WhatsappLogo, color: "#25D366", title: "Promemoria a Famiglia Ferrari", sub: "WhatsApp · domani 09:00" },
    { icon: Bell, color: "#B57C5E", title: "Firma compromesso", sub: "Studio notarile · venerdì 11:00" },
    { icon: MapPin, color: "#7C8B6E", title: "Sopralluogo Isola", sub: "Loft 95 mq · sabato 17:30" },
  ];

  return (
    <div className="space-y-1.5">
      {items.map((it, i) => (
        <div
          key={i}
          className="bg-white border border-[#E5E7E0] rounded-md px-2.5 py-1.5 flex items-center gap-2 transition-all duration-700"
          style={{
            opacity: shown > i ? 1 : 0,
            transform: shown > i ? "translateX(0)" : "translateX(-10px)",
          }}
        >
          <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: `${it.color}1f`, color: it.color }}>
            <it.icon size={13} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] font-semibold text-[#1C1C1A] leading-tight">{it.title}</div>
            <div className="text-[9px] text-[#7C8B6E] mt-[1px] font-medium">{it.sub}</div>
          </div>
          <Check size={11} weight="bold" className="text-[#7C8B6E] shrink-0" />
        </div>
      ))}
      <div
        className="mt-1 px-2.5 py-1 rounded-full inline-flex items-center gap-1 transition-all duration-700"
        style={{
          background: "rgba(124,139,110,0.13)",
          opacity: shown >= 4 ? 1 : 0,
          transform: shown >= 4 ? "translateY(0)" : "translateY(6px)",
        }}
      >
        <Check size={9} weight="bold" className="text-[#5C6E50]" />
        <span className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#5C6E50]">
          Sincronizzato · Google · iCloud
        </span>
      </div>
    </div>
  );
}
